/**
 * OneTrust Debug Utilities
 *
 * Provides debugging functionality to force the OneTrust consent banner to display during testing.
 * When enabled, this module clears all OneTrust-related cookies, storage, and DOM elements,
 * then reinitializes the banner as if the user has never seen it before.
 *
 * Usage:
 * - Call `setOneTrustForceBannerForTestingEnabled(true)` to enable debug mode
 * - Call `forceShowOneTrustBannerForTesting()` to trigger the banner display
 * - Call `isOneTrustForceBannerForTestingEnabled()` to check if debug mode is active
 */

import localStorageService from '../../utils/services/localStorage/localStorage';
import { OT_DATA_SUBJECT_PARAMS_COOKIE_NAME } from '../../../common/utils/cookie/mnCookieConstants';
import { ONE_TRUST_FORCE_BANNER_DEBUG_LOCAL_STORAGE_KEY } from './oneTrustDebugConstants';

const ONE_TRUST_CONSENT_COOKIES = ['OptanonConsent', 'OptanonAlertBoxClosed', OT_DATA_SUBJECT_PARAMS_COOKIE_NAME];
const ONE_TRUST_CONSENT_STORAGE_KEYS = [
    'OptanonConsent',
    'OptanonAlertBoxClosed',
    'OTGPPConsent',
    'OTGPPConsentV2',
    'OTAdditionalConsentString',
    'eupubconsent-v2',
    'euconsent-v2',
    'usprivacy',
    OT_DATA_SUBJECT_PARAMS_COOKIE_NAME,
];
const ONE_TRUST_STORAGE_KEY_PREFIXES = ['optanon', 'ot', 'euconsent', 'eupubconsent', 'usprivacy', 'iab', 'gpp'];
const FORCE_BANNER_LOAD_DELAY_MS = 1000;
let forceBannerLoadTimeout: number | null = null;

export const isOneTrustForceBannerForTestingEnabled = (): boolean =>
    localStorageService.getItem(ONE_TRUST_FORCE_BANNER_DEBUG_LOCAL_STORAGE_KEY) === '1';

const getPathCandidates = (): string[] => {
    const parts = (window.location.pathname || '/').split('/').filter(Boolean);
    return ['/', ...parts.map((_, index) => `/${parts.slice(0, index + 1).join('/')}`)];
};

const getDomainCandidates = (): string[] => {
    const parts = window.location.hostname.split('.');
    return [
        window.location.hostname,
        `.${window.location.hostname}`,
        ...parts.slice(1, -1).map((_, index) => `.${parts.slice(index + 1).join('.')}`),
    ];
};

const clearCookie = (name: string, path: string, domain?: string): void => {
    const domainAttr = domain ? `;domain=${domain}` : '';
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainAttr}`;
};

const getOneTrustCookieNames = (): Set<string> => {
    const existingCookies = document.cookie
        .split(';')
        .map((cookie) => cookie.trim().split('=')[0])
        .filter(Boolean);
    const prefixedCookies = existingCookies.filter((name) =>
        ONE_TRUST_STORAGE_KEY_PREFIXES.some((prefix) => name.toLowerCase().startsWith(prefix)),
    );
    return new Set([...ONE_TRUST_CONSENT_COOKIES, ...prefixedCookies]);
};

const clearOneTrustConsentCookies = (): void => {
    const cookies = getOneTrustCookieNames();
    const paths = getPathCandidates();
    const domains = [undefined, ...getDomainCandidates()];

    cookies.forEach((cookie) => {
        paths.forEach((path) => {
            domains.forEach((domain) => clearCookie(cookie, path, domain));
        });
    });
};

const safelyAccessStorage = (accessor: () => Storage | undefined): Storage | undefined => {
    try {
        return accessor();
    } catch {
        return undefined;
    }
};

const clearStorageKeysByPrefix = (storage: Storage | undefined): void => {
    if (!storage) return;

    const keysToClear = Array.from({ length: storage.length }, (_, index) => storage.key(index))
        .filter(
            (key) =>
                key &&
                key !== ONE_TRUST_FORCE_BANNER_DEBUG_LOCAL_STORAGE_KEY &&
                ONE_TRUST_STORAGE_KEY_PREFIXES.some((prefix) => key.toLowerCase().startsWith(prefix)),
        ) as string[];

    keysToClear.forEach((key) => storage.removeItem(key));
};

const clearOneTrustConsentStorage = (): void => {
    const sessionStorage = safelyAccessStorage(() => window.sessionStorage);

    ONE_TRUST_CONSENT_STORAGE_KEYS.forEach((key) => {
        localStorageService.removeItem(key);
        sessionStorage?.removeItem(key);
    });

    clearStorageKeysByPrefix(safelyAccessStorage(() => window.localStorage));
    clearStorageKeysByPrefix(sessionStorage);
};

const removeOneTrustBannerElements = (): void => {
    ['onetrust-consent-sdk', 'onetrust-banner-sdk', 'onetrust-pc-sdk'].forEach((id) => {
        document.getElementById(id)?.remove();
    });
};

const forceOpenBannerState = (): void => {
    if (!window.OneTrust) return;
    window.OneTrust.IsAlertBoxClosed = () => false;
    window.OneTrust.IsAlertBoxClosedAndValid = () => false;
};

const clearBannerTimeout = (): void => {
    if (forceBannerLoadTimeout !== null) {
        window.clearTimeout(forceBannerLoadTimeout);
        forceBannerLoadTimeout = null;
    }
};

export const forceShowOneTrustBannerForTesting = (): void => {
    if (!isOneTrustForceBannerForTestingEnabled()) return;

    clearOneTrustConsentCookies();
    clearOneTrustConsentStorage();
    removeOneTrustBannerElements();
    forceOpenBannerState();
    window.OneTrust?.Init?.();

    clearBannerTimeout();
    forceBannerLoadTimeout = window.setTimeout(() => {
        window.OneTrust?.LoadBanner?.();
        forceBannerLoadTimeout = null;
    }, FORCE_BANNER_LOAD_DELAY_MS);
};

export const setOneTrustForceBannerForTestingEnabled = (enabled: boolean): void => {
    if (enabled) {
        localStorageService.setItem(ONE_TRUST_FORCE_BANNER_DEBUG_LOCAL_STORAGE_KEY, '1');
    } else {
        localStorageService.removeItem(ONE_TRUST_FORCE_BANNER_DEBUG_LOCAL_STORAGE_KEY);
        clearBannerTimeout();
    }
};
